import { OnInit } from 'angular-ts-decorators';

import { map, each } from 'lodash';
import { Observable } from 'rxjs';

import { CookieStreams } from '@shared/app/scripts/services/cookie-streams';
import { CurrentSession } from '@shared/app/scripts/services/current-session';
import { Domain } from '@shared/app/scripts/services/domain';
import { Initable } from '@shared/app/scripts/services/initable';
import { RouteParamsStreams } from '@core/app/scripts/services/route-params-streams';
import { Session } from '@shared/app/scripts/services/session';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { SidAutoRedirect } from '@core/app/scripts/services/sid-auto-redirect';

/**
 * Parent class that provides shared features for all app components
 * (admin-app, my-app etc.).
 */
export class AppComponent implements OnInit {
  constructor(
    // TODO consider creating dependency wrapper that provides all that is necessary
    private $element: ng.IAugmentedJQuery,
    private $window: ng.IWindowService,
    private routeParamsStreams: RouteParamsStreams,
    protected $q: ng.IQService,
    protected cookieStreams: CookieStreams,
    protected currentSession: CurrentSession,
    protected domain: Domain,
    protected session: Session,
    protected settingsManager: SettingsManager,
    protected sidAutoRedirect: SidAutoRedirect,
  ) {
    this.dependencies = {
      session: session,
      settingsManager: settingsManager,
    };
  }

  /**
   * Object with string keys that matches the name of dependency pointing to
   * dependency as Initable object.
   *
   * Note! Needs to keep always the same reference
   */
  protected dependencies: {
    [name in string]: Initable;
  };

  /**
   * Object with string keys that matches the name of dependency pointing to
   * dependency as Initable object
   */
  public promise: ng.IPromise<any>;

  /**
   * List of all rejected dependecies
   */
  public rejected: string[];

  protected getPromise(name): ng.IPromise<void> {
    const dependency = this.dependencies[name];

    if (!dependency.dependencies) {
      return dependency.init();
    }

    const subDependencies = map(dependency.dependencies, (subDependency) => {
      // NOTE! Simplified approach, it can happen that we will wait for
      // not restarted subDependency
      return this.promises[subDependency];
    });

    return this.$q.all(subDependencies).then(() => {
      return dependency.init();
    });
  }

  private setUpAllPromises(): void {
    each(this.dependencies, (dependency, key) => {
      if (angular.isUndefined(this.promises[key])) {
        this.promises[key] = this.getPromise(key);
      }
    });

    this.promise = this.$q.all(this.promises);

    this.promise
      .then(() => this.$q.resolve())
      .then(angular.noop, () => {
        this.rejected = [];

        each(this.promises, (promise, key) => {
          promise.then(angular.noop, () => {
            this.rejected.push(key);
          });
        });
      });
  }

  private promises: {
    [name in string]: ng.IPromise<void>;
  } = {};

  /**
   * Method to retry to init some of dependencies
   * @param {string[]} list
   * List of all dependencies to be restarted
   * @return {void}
   * Nothing
   */
  public retryMany(list: string[]): void {
    each(list, (name) => {
      this.promises[name] = this.getPromise(name);
    });

    this.setUpAllPromises();
  }

  public ngOnInit() {
    this.setUpAllPromises();

    this.sidAutoRedirect.init();

    this.sidAutoRedirect.unexpectedTenantIdChanges$.subscribe(() => {
      this.promises = {};
      this.setUpAllPromises();
    });

    this.sidAutoRedirect.unexpectedSidChanges$.subscribe((sidValues) => {
      if (
        angular.isUndefined(sidValues.observed) &&
        this.session.redirectAnonymousToGate
      ) {
        this.$window.location.href = this.domain.link('login#/login');
      } else {
        this.currentSession.empty();
        this.promises = {};
        this.setUpAllPromises();
      }
    });

    const darkmode$: Observable<boolean> =
      this.routeParamsStreams.hasFlag('darkmode');

    darkmode$.subscribe((darkmodeValue) => {
      if (darkmodeValue) {
        this.$element.addClass('darkmode');
      } else {
        this.$element.removeClass('darkmode');
      }
    });

    const headless$: Observable<boolean> =
      this.routeParamsStreams.hasFlag('headless');

    headless$.subscribe((headlessValue) => {
      if (headlessValue) {
        this.$element.addClass('headless');
      } else {
        this.$element.removeClass('headless');
      }
    });
  }
}
